/**
 * Pig Latin both ways, behind the Pig Latin Translator.
 *
 * Forward translation throws information away: after `cat` becomes `atcay`, nothing in
 * the word says how many letters moved. Reversing is therefore a heuristic. It moves
 * back the longest run of letters before `ay` that can begin an English word (`c` in
 * `atcay`, `str` in `ingstray`), and leaves a word alone when no consonant cluster can
 * be moved back, which also protects ordinary words that end in "ay" (`day`, `play`).
 */

export type Direction = "to-pig" | "to-english";

const TOKEN_REGEX = /^([^a-zA-Z]*)([a-zA-Z]+)([^a-zA-Z]*)$/;
const WORD_SPLIT_REGEX = /(\s+)/;
const CAPITALIZED_REGEX = /^[A-Z][a-z]*$/;
const ALL_CAPS_REGEX = /^[A-Z]{2,}$/;
const STARTS_WITH_VOWEL_REGEX = /^[aeiou]/i;
const STARTS_WITH_VOWEL_OR_Y_REGEX = /^[aeiouy]/;
const HAS_VOWEL_REGEX = /[aeiouy]/;
const ENDS_WITH_AY_REGEX = /ay$/i;
const ENDS_WITH_W_REGEX = /w$/i;
const MAX_CLUSTER_LENGTH = 3;

const PLAIN_VOWELS = "aeiou";

/** Letter groups that can begin an English word, beyond a single consonant. */
const ONSET_CLUSTERS = new Set([
  "bl",
  "br",
  "ch",
  "cl",
  "cr",
  "dr",
  "dw",
  "fl",
  "fr",
  "gl",
  "gn",
  "gr",
  "kn",
  "ph",
  "pl",
  "pn",
  "pr",
  "ps",
  "qu",
  "rh",
  "sc",
  "sh",
  "sk",
  "sl",
  "sm",
  "sn",
  "sp",
  "st",
  "sw",
  "th",
  "tr",
  "tw",
  "wh",
  "wr",
  "chr",
  "sch",
  "scr",
  "shr",
  "sph",
  "spl",
  "spr",
  "squ",
  "str",
  "thr",
]);

function findFirstVowelIndex(word: string): number {
  for (let i = 0; i < word.length; i += 1) {
    const ch = word[i].toLowerCase();
    if (PLAIN_VOWELS.includes(ch) || (ch === "y" && i > 0)) {
      return i;
    }
  }
  return -1;
}

function applyCase(original: string, translated: string): string {
  if (translated.length === 0) {
    return translated;
  }
  if (ALL_CAPS_REGEX.test(original)) {
    return translated.toUpperCase();
  }
  if (CAPITALIZED_REGEX.test(original)) {
    return translated[0].toUpperCase() + translated.slice(1).toLowerCase();
  }
  return translated;
}

function wordToPigLatin(word: string): string {
  const lower = word.toLowerCase();
  const vowelIndex = findFirstVowelIndex(lower);
  if (vowelIndex === 0) {
    return applyCase(word, `${lower}way`);
  }
  if (vowelIndex === -1) {
    return applyCase(word, `${lower}ay`);
  }
  let cluster = lower.slice(0, vowelIndex);
  let rest = lower.slice(vowelIndex);
  // Treat "qu" as a unit: the u travels with the q.
  if (cluster.endsWith("q") && rest.startsWith("u")) {
    cluster += "u";
    rest = rest.slice(1);
  }
  return applyCase(word, `${rest}${cluster}ay`);
}

function isOnset(letters: string): boolean {
  if (letters.length === 1) {
    return !PLAIN_VOWELS.includes(letters);
  }
  return ONSET_CLUSTERS.has(letters);
}

/** The longest run of letters at the end of `stem` that can begin a word, if one fits. */
function movedCluster(stem: string): string | null {
  for (
    let size = Math.min(MAX_CLUSTER_LENGTH, stem.length - 1);
    size >= 1;
    size -= 1
  ) {
    const cluster = stem.slice(-size);
    const rest = stem.slice(0, -size);
    if (isOnset(cluster) && STARTS_WITH_VOWEL_OR_Y_REGEX.test(rest)) {
      return cluster;
    }
  }
  return null;
}

function wordFromPigLatin(word: string): string {
  const lower = word.toLowerCase();
  if (!ENDS_WITH_AY_REGEX.test(lower) || lower.length <= 2) {
    return word;
  }
  const stem = lower.slice(0, -2);
  if (
    ENDS_WITH_W_REGEX.test(stem) &&
    STARTS_WITH_VOWEL_REGEX.test(stem.slice(0, -1))
  ) {
    return applyCase(word, stem.slice(0, -1));
  }
  if (!HAS_VOWEL_REGEX.test(stem)) {
    return word;
  }
  const cluster = movedCluster(stem);
  if (cluster === null) {
    return word;
  }
  return applyCase(word, `${cluster}${stem.slice(0, -cluster.length)}`);
}

function translateToken(
  token: string,
  translateWord: (word: string) => string
): string {
  const match = token.match(TOKEN_REGEX);
  if (match === null) {
    return token;
  }
  const [, leading, core, trailing] = match;
  return `${leading}${translateWord(core)}${trailing}`;
}

export function translate(text: string, direction: Direction): string {
  const translateWord =
    direction === "to-pig" ? wordToPigLatin : wordFromPigLatin;
  return text
    .split(WORD_SPLIT_REGEX)
    .map((token) => translateToken(token, translateWord))
    .join("");
}
